import React, { useRef, useState } from 'react';
import { css } from '@emotion/react';
import { Checkbox, Divider, IconButton, InputAdornment, ListItemText, Menu, MenuItem, TextField } from '@material-ui/core';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import { colors } from '@/styles';
import { HeadCell, HeadCellWrap, ResizeHandle } from './blocks';

const iconStyle = css`
    font-size: 13px;
    color: ${colors.greens.primary};
`;

// Show the in-menu search once the filter list is long.
const FILTER_SEARCH_THRESHOLD = 8;

// One column header cell (shared by parent + child headers). Label + optional sort/filter kebab
// (when columnOps and the column opts in) + optional drag-to-resize handle.
const ColumnHeader = ({
    column,
    columnOps = false,
    sortDir = null,
    onSort,
    filterOptions = null,
    filterValues = [],
    onFilter,
    resizable = false,
    onResize,
}) => {
    const cellRef = useRef(null);
    const [anchor, setAnchor] = useState(null);
    const [optionSearch, setOptionSearch] = useState('');

    const sortable = columnOps && !!column.sortable;
    const filterable = columnOps && !!filterOptions?.length;
    const hasMenu = sortable || filterable;
    const active = !!sortDir || filterValues.length > 0;

    const showOptionSearch = filterable && filterOptions.length > FILTER_SEARCH_THRESHOLD;
    const term = optionSearch.trim().toLowerCase();
    const visibleOptions =
        filterable && term
            ? filterOptions.filter((opt) => String(opt.label).toLowerCase().includes(term))
            : filterOptions;

    const closeMenu = () => {
        setAnchor(null);
        setOptionSearch('');
    };

    const toggleFilterValue = (value) => {
        const next = filterValues.includes(value)
            ? filterValues.filter((v) => v !== value)
            : [...filterValues, value];
        onFilter(next);
    };

    // Measure current width on drag start so fr/minmax cols resize in px.
    const startResize = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const startX = e.clientX;
        const startWidth = cellRef.current?.getBoundingClientRect().width || 0;
        const onMove = (ev) => onResize(Math.max(48, Math.round(startWidth + (ev.clientX - startX))));
        const onUp = () => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
        };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    };

    return (
        <HeadCellWrap ref={cellRef}>
            <HeadCell active={active}>{column.header}</HeadCell>
            {sortDir === 'asc' ? <ArrowUpwardIcon css={iconStyle} /> : null}
            {sortDir === 'desc' ? <ArrowDownwardIcon css={iconStyle} /> : null}
            {filterValues.length > 0 ? <FilterListIcon css={iconStyle} /> : null}

            {hasMenu ? (
                <>
                    <IconButton
                        size="small"
                        style={{ padding: 2 }}
                        onClick={(e) => setAnchor(e.currentTarget)}
                        aria-label={`${column.header} options`}
                    >
                        <MoreVertIcon style={{ fontSize: 15, color: active ? colors.greens.primary : undefined }} />
                    </IconButton>
                    <Menu
                        anchorEl={anchor}
                        open={!!anchor}
                        onClose={closeMenu}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                        getContentAnchorEl={null}
                        PaperProps={{ style: { maxHeight: 500 } }}
                    >
                        {sortable
                            ? [
                                  <MenuItem
                                      key="asc"
                                      selected={sortDir === 'asc'}
                                      onClick={() => {
                                          onSort('asc');
                                          closeMenu();
                                      }}
                                  >
                                      Sort ascending
                                  </MenuItem>,
                                  <MenuItem
                                      key="desc"
                                      selected={sortDir === 'desc'}
                                      onClick={() => {
                                          onSort('desc');
                                          closeMenu();
                                      }}
                                  >
                                      Sort descending
                                  </MenuItem>,
                                  sortDir ? (
                                      <MenuItem
                                          key="clear-sort"
                                          onClick={() => {
                                              onSort(null);
                                              closeMenu();
                                          }}
                                      >
                                          Clear sort
                                      </MenuItem>
                                  ) : null,
                              ]
                            : null}

                        {sortable && filterable ? <Divider key="divider" /> : null}

                        {showOptionSearch ? (
                            // Stop propagation so typing doesn't hit MenuList type-ahead / close the menu.
                            <li
                                key="filter-search"
                                css={css`
                                    padding: 4px 12px 8px;
                                `}
                                onKeyDown={(e) => e.stopPropagation()}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <TextField
                                    size="small"
                                    fullWidth
                                    variant="outlined"
                                    placeholder="Search"
                                    value={optionSearch}
                                    onChange={(e) => setOptionSearch(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon fontSize="small" />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </li>
                        ) : null}

                        {filterable
                            ? visibleOptions.map((opt) => (
                                  <MenuItem key={opt.value} onClick={() => toggleFilterValue(opt.value)}>
                                      <Checkbox
                                          size="small"
                                          checked={filterValues.includes(opt.value)}
                                          style={{
                                              color: filterValues.includes(opt.value)
                                                  ? colors.greens.primary
                                                  : undefined,
                                          }}
                                      />
                                      <ListItemText primary={opt.label} />
                                  </MenuItem>
                              ))
                            : null}

                        {filterable && filterValues.length > 0 ? (
                            <MenuItem
                                key="clear-filter"
                                onClick={() => {
                                    onFilter([]);
                                    closeMenu();
                                }}
                            >
                                Clear filter
                            </MenuItem>
                        ) : null}
                    </Menu>
                </>
            ) : null}

            {resizable ? <ResizeHandle onMouseDown={startResize} /> : null}
        </HeadCellWrap>
    );
};

export default ColumnHeader;
